// シフト決定画面のカレンダー表示の描画。_calendar.blade.php と同じマークアップを出す
// （全社版は context.showsOrganization で企業名列、context.isAllOrganizations で注記の文言を切り替える）
// id / js-* クラス / data-* 属性は割り当てプレビュー（lecturer_schedule_decision_calendar_preview.js）が参照する

import { buildUrl, classNames, esc } from './html'
import { renderLecturerSelect } from './render_lecturer_select'

const ROLE_UNDECIDED = 'undecided'
const COLUMN_WIDTH_PX = 180
const HEADER_HEIGHT_PX = 44

/**
 * 正時の罫線
 *
 * @param {object} calendar
 * @returns {string}
 */
const hourLines = (calendar) => calendar.hour_marks
    .map((mark) => `<div class="absolute inset-x-0 border-t border-gray-200" style="top: ${esc(mark.top_px)}px;"></div>`)
    .join('')

/**
 * 凡例
 *
 * @param {object} calendar
 * @returns {string}
 */
const renderLegend = (calendar) => {
    const roles = Object.entries(calendar.role_labels)
        .map(([role, label]) => `<span class="inline-block px-3 py-1 rounded border ${esc(calendar.role_classes[role])}">${esc(label)}</span>`)
        .join('')

    return `
    <div class="flex flex-nowrap gap-1 mt-2 mb-2">
        ${roles}
        <span class="inline-block px-3 py-1 rounded border-2 border-red-500 bg-white">時間帯が重複</span>
        <span class="inline-block px-3 py-1 rounded border border-dashed border-gray-400 bg-white">割り当て予定（プレビュー）</span>
    </div>`
}

/**
 * 授業ブロック
 *
 * @param {object} block
 * @returns {string}
 */
const renderBlock = (block) => {
    const undecided = block.role === ROLE_UNDECIDED
    const title = `${block.time_label} ${block.title} ${block.detail}${block.is_overlapping ? '（時間帯が重複）' : ''}`

    return `
    <a href="${undecided ? `#undecided-lesson-${esc(block.id)}` : '#'}"
       class="${classNames('js-calendar-block absolute rounded border overflow-hidden leading-tight px-1', esc(block.class), block.is_overlapping && 'border-2 border-red-500')}"
       data-lesson-id="${esc(block.id)}" data-role="${esc(block.role)}" data-start-minutes="${esc(block.start_minutes)}" data-end-minutes="${esc(block.end_minutes)}"
       style="display: block; top: ${esc(block.top_px)}px; height: ${esc(block.height_px)}px; left: 2px; right: 2px; text-decoration: none; color: inherit;"
       title="${esc(title)}">
        <div class="font-semibold whitespace-nowrap">
            ${esc(block.time_label)}
            <span class="text-gray-600">[${esc(block.role_label)}]</span>
            ${block.is_overlapping ? '<span class="text-red-600 font-bold">重複</span>' : ''}
            ${undecided ? '<span class="text-indigo-700 font-bold js-calendar-assigned-badge" hidden>割当予定</span>' : ''}
        </div>
        <div class="truncate">
            ${block.is_certified_required ? '<span title="認定講師必須">✔︎</span>' : ''}
            ${esc(block.title)} ${esc(block.detail)}
        </div>
    </a>`
}

/**
 * 列（メイン講師未決定 + 講師ごと）
 *
 * @param {object} column
 * @param {object} calendar
 * @returns {string}
 */
const renderColumn = (column, calendar) => `
    <div class="flex-shrink-0 border-r border-gray-300 js-calendar-column" style="width: ${COLUMN_WIDTH_PX}px;"${column.lecturer_id === null ? '' : ` data-lecturer-id="${esc(column.lecturer_id)}"`}>
        <div class="border-b border-gray-300 px-1 py-1 truncate ${column.has_overlap ? 'bg-red-100 text-red-600' : 'bg-gray-100'}" style="height: ${HEADER_HEIGHT_PX}px;" title="${esc(column.title)}">
            <span class="font-bold js-calendar-column-title">${esc(column.title)}</span><br>
            <span class="text-gray-600 js-calendar-column-subtitle">${esc(column.subtitle)}</span>
        </div>
        <div class="relative js-calendar-column-body" style="height: ${esc(calendar.height_px)}px;">
            ${hourLines(calendar)}
            ${column.blocks.map(renderBlock).join('')}
        </div>
    </div>`

/**
 * 時刻の目盛りと列
 *
 * @param {object} calendar
 * @returns {string}
 */
const renderColumns = (calendar) => `
    <div class="w-fit max-w-full overflow-x-auto overflow-y-auto bg-white border border-gray-300">
        <div class="flex flex-nowrap items-start js-calendar-columns" style="min-width: max-content;">
            <div class="flex-shrink-0 sticky left-0 z-10 bg-white border-r border-gray-300" style="width: 56px;">
                <div class="px-1 py-1 font-bold text-gray-600" style="height: ${HEADER_HEIGHT_PX}px;">時刻</div>
                <div class="relative" style="height: ${esc(calendar.height_px)}px;">
                    ${calendar.hour_marks.map((mark) => `<div class="absolute inset-x-0 text-gray-600 text-right pr-1" style="top: ${esc(mark.top_px)}px; margin-top: -7px;">${esc(mark.label)}</div>`).join('')}
                </div>
            </div>
            ${calendar.columns.map((column) => renderColumn(column, calendar)).join('')}
        </div>
    </div>
    <template id="calendar-column-template">
        <div class="flex-shrink-0 border-r border-gray-300 js-calendar-column" style="width: ${COLUMN_WIDTH_PX}px;">
            <div class="border-b border-gray-300 px-1 py-1 truncate bg-indigo-50" style="height: ${HEADER_HEIGHT_PX}px;">
                <span class="font-bold js-calendar-column-title"></span><br>
                <span class="text-gray-600 js-calendar-column-subtitle"></span>
            </div>
            <div class="relative js-calendar-column-body" style="height: ${esc(calendar.height_px)}px;">${hourLines(calendar)}</div>
        </div>
    </template>`

/**
 * 割り当てパネルの1行（メイン講師未決定の授業）
 *
 * @param {object} lesson
 * @param {object} calendar
 * @param {boolean} showsOrganization 企業名列を出すか（全社版）
 * @returns {string}
 */
const renderUndecidedRow = (lesson, calendar, showsOrganization) => {
    const block = calendar.undecided_blocks[lesson.id] || {}
    const td = 'py-2 pr-1 whitespace-nowrap'

    return `
    <tr id="undecided-lesson-${esc(lesson.id)}" class="border-t-2 py-2 ${lesson.is_lesson_require_certified_lecturer ? 'bg-blue-50' : 'bg-white'}"
        data-preview-top="${esc(block.top_px ?? 0)}" data-preview-height="${esc(block.height_px ?? 0)}"
        data-preview-start="${esc(block.start_minutes ?? 0)}" data-preview-end="${esc(block.end_minutes ?? 0)}"
        data-preview-time="${esc(block.time_label ?? '')}" data-preview-title="${esc(block.title ?? '')}" data-preview-detail="${esc(block.detail ?? '')}">
        ${showsOrganization ? `<td class="${td}">${esc(lesson.user_group.organization_name)}</td>` : ''}
        <td class="${td}">${lesson.is_lesson_require_certified_lecturer ? '<label class="checkmark">✔︎</label>' : ''}</td>
        <td class="${td}">${esc(lesson.time_label)}</td>
        <td class="${td}">${esc(lesson.user_group.name)}</td>
        <td class="py-2 pr-4 whitespace-nowrap">${esc(lesson.user_group.field)}</td>
        <td class="${td}">${esc(lesson.number_of_times)}</td>
        <td class="${td}">${esc(lesson.lecture_content)}</td>
        <td class="${td}">${renderLecturerSelect(lesson, 'lecturer_user_id', true)}</td>
        <td class="${td}">${renderLecturerSelect(lesson, 'support_lecturer_user_id', false)}</td>
        <td class="${td}">${renderLecturerSelect(lesson, 'observer_lecturer_user_id', false)}</td>
    </tr>`
}

/**
 * 割り当てパネル（メイン講師未決定の授業。リスト表示と同じ confirm へ送信）
 *
 * @param {object} data
 * @param {{confirmUrl: string, csrf: string, showsOrganization: boolean, isAllOrganizations: boolean}} context
 * @returns {string}
 */
const renderAssignmentPanel = (data, { confirmUrl, csrf, showsOrganization, isAllOrganizations }) => {
    const calendar = data.calendar
    const undecided = data.online_lessons.filter((lesson) => lesson.is_main_lecturer_undecided)
    const th = 'py-2 pr-1 whitespace-nowrap'
    const table = undecided.length === 0
        ? '<p class="text-sm text-gray-600 mt-2">メイン講師未決定の授業はありません。</p>'
        : `
        <table class="text-xs text-center py-2 block overflow-x-auto">
            <thead>
                <tr class="py-2 bg-gray-100">
                    ${showsOrganization ? `<th class="${th}">企業名</th>` : ''}
                    <th class="${th}">認定講師</th><th class="${th}">日時</th><th class="${th}">ユーザーグループ</th><th class="py-2 pr-4 whitespace-nowrap">分野</th>
                    <th class="${th}">実施回数</th><th class="${th}">講義内容</th><th class="${th}">メイン講師</th><th class="${th}">サポート講師</th><th class="${th}">オブザーバー講師</th>
                </tr>
            </thead>
            ${undecided.map((lesson) => renderUndecidedRow(lesson, calendar, showsOrganization)).join('')}
        </table>
        <div class="flex flex-col items-start gap-y-8">
            <input type="submit" name="send" value="入力を確認する" class="admin-indigo-button">
            <p class="text-xs">
                ※ 講師にはチェックが入力されている方のみ選択可能です。メイン講師・サポート講師・オブザーバー講師に同じ方は設定できません。<br>
                ※ <span class="font-bold text-red-500">赤字</span>表示の講師は、同じ日程の対面指導で決定している講師です。日程が被らない講師を設定してください。<br>
                ※ ここで送信されるのはメイン講師未決定の授業だけです。決定済みの授業を変更する場合はリスト表示を使ってください。
            </p>
        </div>`

    return `
    <form id="calendar-assignment-panel" method="post" action="${esc(buildUrl(confirmUrl, data.period.query))}" class="mt-4">
        <input type="hidden" name="_token" value="${esc(csrf)}">
        <p class="font-bold text-sm">メイン講師未決定の授業（${undecided.length}件）</p>
        <ul class="mt-3 list-disc list-inside text-sm text-red-600"></ul>
        ${table}
        <p class="text-xs">
            ※ ${esc(calendar.date_label)} の${isAllOrganizations ? '全組織' : '自組織'}の対面指導 ${esc(calendar.lesson_count)}件を、メイン講師未決定の列と、授業を担当している講師ごとの列に並べています。<br>
            ※ 同じ講師の列で時間帯が被っているブロックは<span class="font-bold text-red-600">赤枠</span>で表示します（重複している講師: ${esc(calendar.overlap_count)}名）。<br>
            ${isAllOrganizations ? '' : '※ 他組織の対面指導は、自組織の講師が担当している時間帯のみ「他組織の対面指導」として表示し、詳細は表示しません。<br>'}
            ※ メイン講師未決定の授業は、下の「メイン講師未決定の授業」で講師を割り当てられます。講師を選ぶとカレンダーに<span class="font-bold">破線のブロック</span>でプレビューされます（保存は「入力を確認する」から）。ブロックをクリックすると該当行へ移動します。
        </p>
    </form>`
}

/**
 * カレンダー表示全体
 *
 * @param {object} data API のレスポンス（calendar を含む）
 * @param {{confirmUrl: string, csrf: string, showsOrganization: boolean, isAllOrganizations: boolean}} context
 * @returns {string}
 */
export const renderCalendar = (data, context) => {
    const calendar = data.calendar

    return `
    <div id="lecturer-schedule-calendar" class="[grid-area:schedule] min-w-0 text-xs" data-height-px="${esc(calendar.height_px)}" data-role-classes="${esc(JSON.stringify(calendar.role_classes))}">
        ${renderLegend(calendar)}
        ${renderColumns(calendar)}
        ${renderAssignmentPanel(data, context)}
    </div>`
}
